import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/primitives';
import {
  useDenik,
  useDenikEntry,
  useDenikKontakty,
  useCreateDenik,
  useUpdateDenik,
  useLockDenik,
  useCopyPreviousDenik,
  useSendDenikApproval,
} from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { StavbaGate } from '../../components/stavba';
import type {
  DenikDataJson,
  DenikEntry,
  DenikKontaktOsoba,
  DenikKontaktRole,
  DenikKontaktyMap,
} from '../../api/schemas';
import styles from './DiaryPage.module.css';
import { DenikDayForm, emptyData, mergeData } from './DenikDayForm';
import { DenikKontaktyModal } from './DenikKontakty';

/**
 * Stavební deník (legacy „kniha" look). Pravá strana = editovatelný formulář
 * přímo na papíře — žádné popupy. Akce (uložit / kopie / zámek / schválení)
 * jsou v toolbaru nahoře.
 */

const CZECH_DAY_NAMES = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'] as const;
const CZECH_MONTHS = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'] as const;

const CONTACT_ROLES: Array<{ key: string; label: string; hasCkait: boolean }> = [
  { key: 'investor', label: 'Investor / objednatel', hasCkait: false },
  { key: 'zhotovitel', label: 'Zhotovitel', hasCkait: true },
  { key: 'tds', label: 'Technický dozor stavebníka (TDS)', hasCkait: true },
  { key: 'autorsky_dozor', label: 'Autorský dozor', hasCkait: true },
  { key: 'bozp_koordinator', label: 'Koordinátor BOZP', hasCkait: false },
];

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseEntryDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ensureKontaktyShape(raw: DenikKontaktyMap | undefined | null): DenikKontaktyMap {
  const out: DenikKontaktyMap = {};
  for (const r of CONTACT_ROLES) {
    const src = raw?.[r.key] ?? {};
    let osoby: DenikKontaktOsoba[] = Array.isArray(src.osoby) ? src.osoby : [];
    if (
      osoby.length === 0 &&
      ((src as { osoba?: string }).osoba || (src as { tel?: string }).tel || (src as { email?: string }).email)
    ) {
      const legacy = src as { osoba?: string; tel?: string; email?: string; ckait?: string };
      osoby = [
        {
          jmeno: legacy.osoba ?? '',
          tel: legacy.tel ?? '',
          email: legacy.email ?? '',
          ckait: legacy.ckait ?? '',
          aktivni: true,
        },
      ];
    }
    out[r.key] = {
      firma: src.firma ?? '',
      ico: src.ico ?? '',
      dic: src.dic ?? '',
      osoby,
    };
  }
  return out;
}

function normalizePodpisSrc(v: string | undefined): string | null {
  const s = (v ?? '').trim();
  if (!s) return null;
  if (s.startsWith('data:image/')) return s;
  if (/^[A-Za-z0-9+/]+=*$/.test(s) && s.length > 200) return `data:image/png;base64,${s}`;
  return null;
}

export function DiaryPage() {
  const { activeStavba } = useActiveStavba();
  const stavbaId = activeStavba?.id ?? null;

  const { data: entries = [], isLoading, error } = useDenik(stavbaId);
  const kontaktyQuery = useDenikKontakty(stavbaId);
  const kontakty = useMemo(() => ensureKontaktyShape(kontaktyQuery.data), [kontaktyQuery.data]);

  const [selectedIso, setSelectedIso] = useState<string>(() => toIsoDate(new Date()));
  const [kontaktyOpen, setKontaktyOpen] = useState(false);

  const entriesByIso = useMemo(() => {
    const m = new Map<string, DenikEntry>();
    for (const e of entries) {
      const d = parseEntryDate(e.datum);
      if (d) m.set(toIsoDate(d), e);
    }
    return m;
  }, [entries]);

  const selectedDate = useMemo(() => {
    const [y, mo, d] = selectedIso.split('-').map(Number);
    return new Date(y, mo - 1, d);
  }, [selectedIso]);

  const selectedEntry = entriesByIso.get(selectedIso) ?? null;

  // Detail entry (data_json) pro vybraný záznam
  const detailQuery = useDenikEntry(stavbaId, selectedEntry?.id ?? null);
  const loadedEntry = detailQuery.data ?? null;
  const locked = loadedEntry?.uzamceno ?? selectedEntry?.uzamceno ?? false;

  // Lokální stav formuláře (s dirty trackingem)
  const [data, setData] = useState<DenikDataJson>(() => emptyData());
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Synchronizace stavu s detailem entry
  const lastLoadedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedEntry && loadedEntry && loadedEntry.id === selectedEntry.id) {
      // Nahraj pouze když se ID změnilo (jinak přepíšu rozdělanou editaci)
      if (lastLoadedIdRef.current !== loadedEntry.id) {
        setData(mergeData(loadedEntry.data_json));
        setDirty(false);
        setSavedAt(null);
        lastLoadedIdRef.current = loadedEntry.id;
      }
    } else if (!selectedEntry) {
      // Žádný záznam pro tento den — prázdná data
      setData(emptyData());
      setDirty(false);
      setSavedAt(null);
      lastLoadedIdRef.current = null;
    }
  }, [selectedEntry, loadedEntry]);

  // Mutace
  const create = useCreateDenik(stavbaId);
  const update = useUpdateDenik(stavbaId);
  const lockMut = useLockDenik(stavbaId);
  const copyPrev = useCopyPreviousDenik(stavbaId);
  const sendApproval = useSendDenikApproval(stavbaId);

  const saving = create.isPending || update.isPending;

  function handleDataChange(next: DenikDataJson) {
    setData(next);
    setDirty(true);
    setSavedAt(null);
  }

  async function handleSave() {
    if (!stavbaId) return;
    setErrMsg(null);
    try {
      if (selectedEntry) {
        await update.mutateAsync({ denikId: selectedEntry.id, payload: { data_json: data } });
      } else {
        await create.mutateAsync({ datum: selectedIso, data_json: data });
      }
      setDirty(false);
      setSavedAt(Date.now());
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Nepodařilo se uložit zápis.');
    }
  }

  async function handleCreateForDay() {
    if (!stavbaId) return;
    setErrMsg(null);
    try {
      await create.mutateAsync({ datum: selectedIso, data_json: emptyData() });
      // detailQuery se invalidovala, useEffect natáhne nová data
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Nepodařilo se založit zápis.');
    }
  }

  async function handleCopyPrevious() {
    if (!selectedEntry) return;
    if (!window.confirm('Zkopírovat pracovníky/práce/materiály/stroje z předchozího dne? Aktuální obsah dnešního dne bude nahrazen.')) return;
    setErrMsg(null);
    try {
      const updated = await copyPrev.mutateAsync(selectedEntry.id);
      setData(mergeData(updated.data_json));
      setDirty(false);
      lastLoadedIdRef.current = updated.id;
      setSavedAt(Date.now());
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Nepodařilo se kopírovat.');
    }
  }

  async function handleLockToggle() {
    if (!selectedEntry) return;
    setErrMsg(null);
    try {
      await lockMut.mutateAsync({ denikId: selectedEntry.id, lock: !locked });
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Nepodařilo se změnit zámek.');
    }
  }

  async function handleSendApproval() {
    if (!selectedEntry) return;
    setErrMsg(null);
    try {
      await sendApproval.mutateAsync(selectedEntry.id);
      setSavedAt(Date.now());
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : 'Nepodařilo se odeslat ke schválení.');
    }
  }

  function jumpToIso(iso: string) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    if (dirty) {
      // tichý uložení neděláme — ať si uživatel rozmyslí
      if (!window.confirm('Máte neuložené změny. Opustit den bez uložení?')) return;
    }
    setSelectedIso(iso);
    setDirty(false);
  }

  function navigate(deltaDays: number) {
    const dt = new Date(selectedDate);
    dt.setDate(dt.getDate() + deltaDays);
    jumpToIso(toIsoDate(dt));
  }

  if (!activeStavba) {
    return (
      <div className={styles.root}>
        <StavbaGate message="Stavební deník se vede pro jednu stavbu." />
      </div>
    );
  }

  // Status pill
  const statusPill = dirty ? (
    <span className={`${styles.statusPill} ${styles.statusPillDirty}`}>● Neuložené změny</span>
  ) : savedAt && Date.now() - savedAt < 6000 ? (
    <span className={`${styles.statusPill} ${styles.statusPillSaved}`}>✓ Uloženo</span>
  ) : selectedEntry ? (
    <span className={`${styles.statusPill} ${styles.statusPillIdle}`}>
      Zápis #{selectedEntry.cislo}
    </span>
  ) : null;

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarTitle}>
          <strong>Stavební deník</strong>
          <span className={styles.toolbarSub}>{activeStavba.nazev}</span>
        </div>

        <Button variant="secondary" size="sm" onClick={() => jumpToIso(toIsoDate(new Date()))}>
          Dnes
        </Button>

        <input
          className={styles.dateJump}
          type="date"
          value={selectedIso}
          onChange={(e) => jumpToIso(e.target.value)}
          title="Skok na datum"
        />

        <div className={styles.nav}>
          <button className={styles.navBtn} type="button" onClick={() => navigate(-1)} title="Předchozí den">
            ←
          </button>
          <button className={styles.navBtn} type="button" onClick={() => navigate(1)} title="Následující den">
            →
          </button>
        </div>

        <div className={styles.toolbarSep} />
        {statusPill}

        <div className={styles.toolbarRight}>
          <Button variant="secondary" size="sm" onClick={() => setKontaktyOpen(true)}>
            Oddíl A
          </Button>
          {selectedEntry && !locked && (
            <Button variant="secondary" size="sm" onClick={handleCopyPrevious} disabled={copyPrev.isPending}>
              📋 Kopie z předchozího
            </Button>
          )}
          {selectedEntry && (
            <Button variant="secondary" size="sm" onClick={handleSendApproval} disabled={sendApproval.isPending}>
              ✉️ Schválení
            </Button>
          )}
          {selectedEntry && !locked && (
            <Button variant="secondary" size="sm" onClick={handleLockToggle} disabled={lockMut.isPending}>
              🔒 Zamknout
            </Button>
          )}
          {selectedEntry && locked && (
            <Button variant="secondary" size="sm" onClick={handleLockToggle} disabled={lockMut.isPending}>
              🔓 Odemknout
            </Button>
          )}
          {selectedEntry && (
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || locked || !dirty}>
              {saving ? 'Ukládám…' : dirty ? 'Uložit' : 'Uloženo'}
            </Button>
          )}
        </div>
      </div>

      {errMsg && <div className={styles.errorBox}>{errMsg}</div>}

      {isLoading ? (
        <div className={styles.statusLine}>Načítám záznamy…</div>
      ) : error ? (
        <div className={styles.errorBox}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <div className={styles.book}>
          <div className={styles.spine} />

          <div className={styles.pageContainer}>
            {/* LEVÁ STRANA — Oddíl A */}
            <div className={`${styles.page} ${styles.pageLeft}`}>
              <div className={styles.leftHeader}>
                <div className={styles.leftTitle}>STAVEBNÍ DENÍK</div>
                <div className={styles.leftSubtitle}>Oddíl A — Identifikační údaje</div>
              </div>

              <div className={styles.aBox}>
                <div className={styles.aLabel}>Stavba</div>
                <div className={styles.aName}>{activeStavba.nazev}</div>
                {activeStavba.adresa && <div className={styles.aAddr}>{activeStavba.adresa}</div>}
                {activeStavba.stavebni_povoleni && (
                  <div className={styles.aPermit}>Povolení: {activeStavba.stavebni_povoleni}</div>
                )}
              </div>

              <div className={styles.sectionHeader}>OSOBY A AUTORIZACE</div>

              {kontaktyQuery.isLoading ? (
                <div className={styles.leftHint}>Načítám Oddíl A…</div>
              ) : kontaktyQuery.error ? (
                <div className={styles.leftHint}>Oddíl A se nepodařilo načíst.</div>
              ) : (
                <div className={styles.roles}>
                  {CONTACT_ROLES.map((r) => {
                    const role: DenikKontaktRole = kontakty[r.key] ?? {};
                    const osoby = role.osoby ?? [];
                    const active = osoby.filter((o) => o.aktivni !== false);
                    const show = active.length > 0 ? active : osoby.slice(0, 1);
                    return (
                      <div key={r.key} className={styles.roleBlock}>
                        <div className={styles.roleHead}>
                          <div className={styles.roleLabel}>{r.label}</div>
                          {(role.firma || role.ico || role.dic) && (
                            <div className={styles.roleCompany}>
                              {role.firma ? <span>{role.firma}</span> : null}
                              {role.ico ? <span> IČO {role.ico}</span> : null}
                              {role.dic ? <span> · DIČ {role.dic}</span> : null}
                            </div>
                          )}
                        </div>

                        {show.length === 0 ? (
                          <div className={styles.roleEmpty}>—</div>
                        ) : (
                          show.map((o, idx) => {
                            const podpisSrc = normalizePodpisSrc(o.podpis);
                            return (
                              <div key={idx} className={styles.personRow}>
                                <div className={styles.personInfo}>
                                  <div className={styles.personName}>{o.jmeno || '—'}</div>
                                  <div className={styles.personMeta}>
                                    {o.tel ? <span>{o.tel}</span> : null}
                                    {o.email ? <span>{o.email}</span> : null}
                                    {r.hasCkait && o.ckait ? <span>ČKAIT {o.ckait}</span> : null}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className={styles.stamp}
                                  onClick={() => setKontaktyOpen(true)}
                                  title="Klikněte pro úpravu Oddílu A"
                                >
                                  {podpisSrc ? (
                                    <img className={styles.stampImg} src={podpisSrc} alt="Razítko / podpis" />
                                  ) : (
                                    <span className={styles.stampPlaceholder}>Razítko / podpis</span>
                                  )}
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className={styles.leftHint}>
                Úpravy osob, autorizačních razítek a podpisů uděláte přes <b>Oddíl A</b> (klik na razítko taky otevře editaci).
              </div>

              <div className={styles.pageNumber} style={{ left: 16 }}>A</div>
            </div>

            {/* PRAVÁ STRANA — denní zápis (přímo editovatelný) */}
            <div className={`${styles.page} ${styles.pageRight}`}>
              <div className={styles.rightTop}>
                <div className={styles.dateHeader}>
                  {CZECH_DAY_NAMES[selectedDate.getDay()]},{' '}
                  {selectedDate.getDate()}. {CZECH_MONTHS[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                </div>
                {locked && <div className={styles.lockBadge}>🔒 ZAMČENO</div>}
              </div>

              <div className={styles.rightMeta}>
                {selectedEntry ? (
                  <>
                    Strana <strong>{selectedEntry.cislo}</strong>
                    <span className={styles.dotSep}>•</span>
                    Fotky: {selectedEntry.photos_count}
                    <span className={styles.dotSep}>•</span>
                    Vytvořeno: {parseEntryDate(selectedEntry.created_at)?.toLocaleDateString('cs-CZ') ?? '—'}
                  </>
                ) : (
                  <>Pro tento den zatím není zápis.</>
                )}
              </div>

              {!selectedEntry ? (
                <div className={styles.emptyDay}>
                  <div className={styles.emptyDayIcon}>📝</div>
                  <div className={styles.emptyDayText}>
                    Pro tento den zatím není zápis. Po založení můžete přímo doplňovat počasí, pracovníky, práce, materiály a podpisy.
                  </div>
                  <button
                    type="button"
                    className={styles.emptyDayBtn}
                    onClick={handleCreateForDay}
                    disabled={create.isPending}
                  >
                    {create.isPending ? 'Zakládám…' : '+ Založit zápis pro tento den'}
                  </button>
                </div>
              ) : detailQuery.isLoading ? (
                <div className={styles.statusLine}>Načítám detail dne…</div>
              ) : (
                <DenikDayForm
                  stavbaId={activeStavba.id}
                  entry={selectedEntry}
                  isoDate={selectedIso}
                  data={data}
                  onChange={handleDataChange}
                  disabled={locked}
                />
              )}

              <div className={styles.pageNumber} style={{ right: 16 }}>
                {selectedEntry ? selectedEntry.cislo : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      <DenikKontaktyModal
        open={kontaktyOpen}
        onClose={() => setKontaktyOpen(false)}
        stavbaId={activeStavba.id}
      />
    </div>
  );
}
